import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

function DesgloseItem({ label, value, color, hint }) {
  return (
    <div title={hint} style={{ display: 'flex', flexDirection: 'column', gap: 2, cursor: 'help' }}>
      <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 500 }}>{label}</span>
      <span style={{ color, fontFamily: 'Space Grotesk', fontSize: 22, fontWeight: 700 }}>{(value || 0).toLocaleString('es-ES')}</span>
    </div>
  );
}


function KpiCard({ title, value, sub, icon, color, delay = 0, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      data-testid={testId}
      style={{
        background: '#1e293b', border: '1px solid #334155',
        borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '12px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 500 }}>{title}</span>
        <div style={{
          width: '36px', height: '36px', borderRadius: '8px',
          background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color
        }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '32px', fontWeight: 700, color: '#f1f5f9', fontFamily: 'Space Grotesk', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && <div style={{ color: '#64748b', fontSize: '12px' }}>{sub}</div>}
    </motion.div>
  );
}

const SHIFT_COLORS = { 'Mañana': '#60a5fa', 'Tarde': '#f97316', 'Noche': '#a78bfa' };
const STATUS_COLORS = { 'Correcto': '#22c55e', 'Fichaje cerrado automáticamente': '#f97316', 'Falta fichaje salida': '#ef4444' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '10px 14px' }}>
      <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: '13px', margin: '2px 0' }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('es-ES') : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { apiFetch } = useAuth();
  const { t } = useLang();
  const [summary, setSummary] = useState(null);
  const [trends, setTrends]   = useState([]);
  const [byCentro, setByCentro] = useState([]);
  const [loading, setLoading] = useState(true);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const dateLabel = yesterday.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [s, tr, bc] = await Promise.all([
          apiFetch('/api/dashboard/summary').then(r => r.json()),
          apiFetch('/api/dashboard/trends').then(r => r.json()),
          apiFetch('/api/dashboard/by-centro').then(r => r.json()),
        ]);
        setSummary(s);
        setTrends(tr);
        setByCentro(bc);
      } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
      setLoading(false);
    };
    load();
  }, [apiFetch]);

  const shiftData = summary ? [
    { name: t('manana'), value: summary.manana || 0, color: '#60a5fa' },
    { name: t('tarde'),  value: summary.tarde  || 0, color: '#f97316' },
    { name: t('noche'),  value: summary.noche  || 0, color: '#a78bfa' },
  ] : [];

  const statusData = summary ? [
    { name: t('correcto'),    value: summary.correctos    || 0, color: '#22c55e' },
    { name: t('autoCerrado'), value: summary.auto_cerrados|| 0, color: '#f97316' },
    { name: t('faltaFichaje'),value: summary.error        || 0, color: '#ef4444' },
  ] : [];

  if (loading) {
    return (
      <div style={{ padding: '32px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', height: '120px', animation: 'pulse 2s ease-in-out infinite' }} />
          ))}
        </div>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Dashboard</h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
          Datos de <strong style={{ color: '#94a3b8', textTransform: 'capitalize' }}>{dateLabel}</strong>
        </p>
      </div>

      {/* KPI Cards — desglose Phase 13: turnos vs empleados únicos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <KpiCard
          testId="kpi-correctos"
          title="Turnos correctos"
          value={(summary?.correctos || 0).toLocaleString('es-ES')}
          sub={`${summary?.total ? Math.round((summary.correctos / summary.total) * 100) : 0}% del total · ${(summary?.empleados_correctos || 0).toLocaleString('es-ES')} empleados`}
          color="#22c55e"
          delay={0}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
        />
        <KpiCard
          testId="kpi-auto-cerrados"
          title="Turnos sin salida"
          value={(summary?.auto_cerrados || 0).toLocaleString('es-ES')}
          sub={`${summary?.total ? Math.round((summary.auto_cerrados / summary.total) * 100) : 0}% del total · autocerrados`}
          color="#f97316"
          delay={0.06}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
        />
        <KpiCard
          testId="kpi-empleados-sin-salida"
          title="Empleados sin salida"
          value={(summary?.empleados_sin_salida || 0).toLocaleString('es-ES')}
          sub={`Personas únicas que sólo ficharon una vez`}
          color="#fbbf24"
          delay={0.12}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>}
        />
        <KpiCard
          testId="kpi-empleados-incidencia"
          title="Empleados con incidencia"
          value={(summary?.empleados_incidencia || 0).toLocaleString('es-ES')}
          sub={`Al menos 1 turno sin cerrar bien`}
          color="#a78bfa"
          delay={0.18}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>}
        />
      </div>

      {/* Second row — desglose y contexto */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <KpiCard
          testId="kpi-horas-totales"
          title={t('horasTotales')}
          value={`${(summary?.total_horas || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })}h`}
          sub={`Media: ${summary?.avg_horas || 0}h/empleado`}
          color="#60a5fa"
          delay={0.24}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}
        />
        <KpiCard
          testId="kpi-distribucion-turno"
          title="Total turnos registrados"
          value={(summary?.total || 0).toLocaleString('es-ES')}
          sub={`${shiftData.find(s => s.value === Math.max(...shiftData.map(d => d.value)))?.name || '-'} es el turno principal`}
          color="#94a3b8"
          delay={0.30}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
        />
        {/* Desglose de turnos sin salida */}
        {summary?.autoclose_desglose && (
          <div data-testid="autoclose-desglose" style={{ gridColumn: 'span 2', background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Desglose “turnos sin salida”
              <span title="A: la persona sólo ha fichado 1 vez ese día · B: fichó varias veces pero todas quedaron sin pareja E-S · C: turno partido con al menos uno correcto y otro sin cerrar" style={{ marginLeft: 6, color: '#475569', cursor: 'help' }}>ⓘ</span>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <DesgloseItem label="A · 1 fichaje total" value={summary.autoclose_desglose.A || 0} color="#fbbf24" hint="La persona sólo ha fichado una vez ese día" />
              <DesgloseItem label="B · Sin pareja E-S" value={summary.autoclose_desglose.B || 0} color="#f97316" hint="Fichó varias veces, pero ningún turno formó entrada+salida" />
              <DesgloseItem label="C · Turno partido incompleto" value={summary.autoclose_desglose.C || 0} color="#a78bfa" hint="Tiene turnos correctos + al menos 1 turno sin cerrar" />
            </div>
          </div>
        )}
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {/* Bar chart by shift */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          data-testid="chart-bar-turno"
          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}
        >
          <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>
            {t('distribucionTurno')}
          </h3>
          {shiftData.every(d => d.value === 0) ? (
            <div style={{ textAlign: 'center', color: '#475569', padding: '40px 0', fontSize: '14px' }}>Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={shiftData} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Empleados" radius={[6, 6, 0, 0]}>
                  {shiftData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Donut by status */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          data-testid="chart-donut-estado"
          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}
        >
          <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>Estado fichajes</h3>
          {statusData.every(d => d.value === 0) ? (
            <div style={{ textAlign: 'center', color: '#475569', padding: '40px 0', fontSize: '14px' }}>Sin datos</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                    dataKey="value" paddingAngle={3}>
                    {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                {statusData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: d.color, flexShrink: 0 }} />
                      <span style={{ color: '#94a3b8', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>{d.name}</span>
                    </div>
                    <span style={{ color: '#f1f5f9', fontSize: '12px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{d.value.toLocaleString('es-ES')}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Trend Chart */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        data-testid="chart-line-tendencia"
        style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}
      >
        <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>Tendencia 14 días</h3>
        {trends.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '40px 0', fontSize: '14px' }}>Sin datos de tendencia aún</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
              <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }}
                tickFormatter={d => d ? d.slice(5) : ''} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
              <Line type="monotone" dataKey="correctos" name="Correctos" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="auto_cerrados" name="Turnos sin salida" stroke="#f97316" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="total" name="Total" stroke="#60a5fa" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </motion.div>

      {/* Top 10 Centros */}
      {byCentro.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}
        >
          <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '16px' }}>Top 10 Centros</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {byCentro.slice(0, 10).map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#475569', fontSize: '12px', width: '20px', textAlign: 'right', fontFamily: 'IBM Plex Mono' }}>{i+1}</span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#cbd5e1', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{c.centro || 'Sin centro'}</span>
                    <span style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 600, fontFamily: 'IBM Plex Mono' }}>{c.total}</span>
                  </div>
                  <div style={{ height: '4px', background: '#0f172a', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(c.total / (byCentro[0]?.total || 1)) * 100}%`, background: '#60a5fa', borderRadius: '2px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
